import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { DialogHeaderBand } from "@/components/dialogs/DialogHeaderBand";
import { KINDS } from "@/components/dialogs/kinds";
import { cn } from "@/lib/utils";

/**
 * Tells the person something stopped or needs attention. Never offers the blocked action. `items` is a list of
 * { label, count, href? } shown as a two-column grid with count badges; it scrolls when it is long.
 */
export function NoticeDialog({ open, onOpenChange, kind = "error", title, description, items, itemsHeading, footnote, dismissLabel = "Got it" }) {
  const k = KINDS[kind] || KINDS.error;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg gap-0 overflow-hidden p-0" data-testid="notice-dialog">
        <DialogHeaderBand kind={kind}>
          <DialogTitle className="text-base">{title}</DialogTitle>
          {description && <DialogDescription className="whitespace-pre-line text-sm">{description}</DialogDescription>}
        </DialogHeaderBand>
        {items?.length > 0 && (
          <div className="px-6 pt-4">
            {itemsHeading && <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{itemsHeading}</p>}
            <ul className="grid max-h-[40vh] grid-cols-1 gap-2 overflow-y-auto pr-1 sm:grid-cols-2" data-testid="notice-items">
              {items.map((it) => (
                <li key={it.key || it.label} className="flex items-center justify-between gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm">
                  {it.href
                    ? <Link to={it.href} onClick={() => onOpenChange?.(false)} className="inline-flex min-w-0 items-center gap-1 truncate text-foreground underline-offset-2 hover:underline">{it.label}<ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden="true" /></Link>
                    : <span className="min-w-0 truncate text-foreground">{it.label}</span>}
                  {it.count != null && <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold", k.badge)}>{it.count}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
        {footnote && <p className="px-6 pt-3 text-xs text-muted-foreground">{footnote}</p>}
        <div className="flex justify-end px-6 py-4">
          <Button type="button" onClick={() => onOpenChange?.(false)} data-testid="notice-dialog-dismiss">{dismissLabel}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
